<template>
	<div>
		<p class="small text-body-secondary mb-3">
			{{ technicianLabel(route.technicianId, users) }} ·
			{{ routeWindowText(route) }}
			<template v-if="route.dispatchedAt">
				· dispatched {{ formatDate(new Date(route.dispatchedAt)) }}
			</template>
			<template v-if="route.completedAt">
				· completed {{ formatDate(new Date(route.completedAt)) }}
			</template>
		</p>

		<div class="d-flex flex-wrap gap-2 mb-3">
			<button
				v-if="route.state === 'draft'"
				type="button"
				class="btn btn-sm btn-primary"
				:disabled="busy"
				@click="dispatchRoute"
			>
				<i class="bi bi-send me-1"></i>Dispatch
			</button>
			<span
				v-if="!editable"
				class="small text-body-secondary align-self-center"
			>
				This Route is {{ route.state }}; §8 Rule 6 keeps its membership and
				sequence as the historical record.
			</span>
		</div>

		<div v-if="editable" class="input-group input-group-sm mb-3">
			<select
				v-model.number="addAssignmentId"
				class="form-select"
				aria-label="Assignment to add"
				:disabled="busy"
			>
				<option :value="0" disabled>
					Add one of this technician's draft assignments…
				</option>
				<option
					v-for="assignment in addable"
					:key="assignment.id"
					:value="assignment.id"
				>
					{{ assignmentLabel(assignment) }}
				</option>
			</select>
			<button
				type="button"
				class="btn btn-outline-primary"
				:disabled="busy || addAssignmentId <= 0"
				@click="addMember"
			>
				Add
			</button>
		</div>

		<div class="row g-3">
			<div class="col-12 col-lg-7">
				<h3 class="h6">Sequence</h3>
				<p v-if="ordered.length === 0" class="small text-body-secondary mb-2">
					No member holds a position yet — drop a row here to sequence it.
				</p>
				<ol
					v-else
					class="list-group list-group-numbered mb-2"
					@dragover.prevent
					@drop.prevent="dropAt(ordered.length)"
				>
					<li
						v-for="(member, index) in ordered"
						:key="member.assignmentId"
						class="list-group-item d-flex justify-content-between align-items-center py-1"
						:draggable="editable"
						@dragstart="dragId = member.assignmentId"
						@dragend="dragId = null"
						@dragover.prevent
						@drop.prevent.stop="dropAt(index)"
					>
						<button
							type="button"
							class="btn btn-link btn-sm p-0 text-start text-truncate"
							@click="
								emit('select', { kind: 'assignment', id: member.assignmentId })
							"
						>
							{{ memberLabel(member.assignmentId) }}
						</button>
						<button
							v-if="editable"
							type="button"
							class="btn btn-outline-danger btn-sm"
							:disabled="busy"
							title="Remove from route"
							@click="removeMember(member.assignmentId)"
						>
							<i class="bi bi-x-lg"></i>
						</button>
					</li>
				</ol>
				<p v-if="editable && ordered.length > 0" class="form-text">
					Drag a row onto another to state the order; the whole sequence is
					written in one call.
				</p>
			</div>

			<div class="col-12 col-lg-5">
				<h3 class="h6">Unsequenced</h3>
				<p
					v-if="unsequenced.length === 0"
					class="small text-body-secondary mb-2"
				>
					Every member holds a position.
				</p>
				<ul v-else class="list-group list-group-flush mb-2">
					<li
						v-for="member in unsequenced"
						:key="member.assignmentId"
						class="list-group-item d-flex justify-content-between align-items-center py-1 px-0"
						:draggable="editable"
						@dragstart="dragId = member.assignmentId"
						@dragend="dragId = null"
					>
						<button
							type="button"
							class="btn btn-link btn-sm p-0 text-start text-truncate"
							@click="
								emit('select', { kind: 'assignment', id: member.assignmentId })
							"
						>
							{{ memberLabel(member.assignmentId) }}
						</button>
						<button
							v-if="editable"
							type="button"
							class="btn btn-outline-danger btn-sm"
							:disabled="busy"
							title="Remove from route"
							@click="removeMember(member.assignmentId)"
						>
							<i class="bi bi-x-lg"></i>
						</button>
					</li>
				</ul>
				<p v-if="editable && unsequenced.length > 0" class="form-text">
					Drag an unsequenced member onto the sequence to place it.
				</p>
			</div>
		</div>

		<!-- The drive plan: a projection of the Route, not a property of it
		     (routing design §7), so it is stated under the membership it is read
		     from and no action above it waits on one. A plan the engine could not
		     compute reads as what it is — the straight line the map already
		     draws — never as an error in the Route. -->
		<div class="mt-3 pt-3 border-top">
			<div class="d-flex justify-content-between align-items-center mb-2">
				<h3 class="h6 mb-0">Drive plan</h3>
				<span
					v-if="plan && plan.status !== 'ok'"
					class="badge"
					:class="planStatusClass"
				>
					{{ planStatusLabel }}
				</span>
			</div>

			<p v-if="plan === null" class="small text-body-secondary mb-0">
				No plan yet — the Route draws its stops as a straight line until one is
				computed.
			</p>

			<template v-else>
				<p
					v-if="plan.status === 'unavailable'"
					class="small text-warning-emphasis mb-2"
				>
					The routing engine is down, so this Route draws as a straight line.
				</p>
				<p v-else-if="plan.status === 'failed'" class="small text-danger mb-2">
					{{ plan.error ?? "The routing engine rejected this Route." }}
				</p>
				<p
					v-else-if="plan.status === 'partial'"
					class="small text-warning-emphasis mb-2"
				>
					{{
						plan.error ?? "Some stops could not be placed on the road network."
					}}
				</p>

				<p v-if="planSummary" class="small mb-2">{{ planSummary }}</p>

				<p
					v-if="planStops.length === 0 && ordered.length === 0"
					class="small text-body-secondary mb-2"
				>
					No member holds a position yet, so there is nothing to drive in order.
				</p>
				<ul
					v-if="planStops.length > 0"
					class="list-group list-group-flush mb-2"
				>
					<li
						v-for="stop in planStops"
						:key="stop.assignmentId"
						class="list-group-item py-1 px-0"
					>
						<div
							class="d-flex justify-content-between align-items-baseline gap-2"
						>
							<button
								type="button"
								class="btn btn-link btn-sm p-0 text-start text-truncate"
								@click="
									emit('select', {
										kind: 'assignment',
										id: stop.assignmentId,
									})
								"
							>
								#{{ stop.position }} · {{ memberLabel(stop.assignmentId) }}
							</button>
							<span class="small text-nowrap">{{ etaText(stop) }}</span>
						</div>
						<div
							class="small text-body-secondary d-flex flex-wrap align-items-center gap-2"
						>
							<span>Service {{ serviceText(stop) }}</span>
							<span v-if="stop.legSeconds !== null">
								· {{ formatDuration(stop.legSeconds) }} drive
							</span>
							<span
								v-if="stop.arrivalState === 'late'"
								class="badge text-bg-warning"
							>
								late
							</span>
							<span
								v-if="stop.arrivalState === 'unroutable'"
								class="badge text-bg-secondary"
							>
								unroutable
							</span>
							<span
								v-if="stop.arrivalState === 'no_point'"
								class="badge text-bg-secondary"
							>
								no point
							</span>
						</div>
					</li>
				</ul>

				<p v-if="returnText" class="small mb-1">{{ returnText }}</p>
				<p v-if="plan.unsequenced > 0" class="small text-warning-emphasis mb-1">
					{{ plan.unsequenced }}
					{{ plan.unsequenced === 1 ? "member is" : "members are" }}
					unsequenced, so this plan leaves
					{{ plan.unsequenced === 1 ? "it" : "them" }} out.
				</p>
			</template>

			<!-- Suggesting is a read; the only write it can lead to is the
			     existing reorder door, so the proposal is shown as a diff of the
			     positions the operator already stated and never applied by
			     itself. -->
			<div class="mt-2">
				<button
					v-if="suggestion === null && canSuggest"
					type="button"
					class="btn btn-sm btn-outline-primary"
					:disabled="busy"
					@click="suggestOrder"
				>
					<i class="bi bi-shuffle me-1"></i>Suggest order
				</button>

				<div v-else-if="suggestion" class="border rounded p-2">
					<p v-if="!suggestion.improved" class="small mb-2">
						The current order is already the best it can find.
					</p>
					<template v-else>
						<p class="small mb-1">
							A drive in this order finishes
							<strong>
								{{ formatDate(new Date(suggestion.finishesAt)) }}
							</strong>
							.
						</p>
						<ul class="list-unstyled small mb-2">
							<li
								v-for="(assignmentId, index) in suggestion.assignmentIds"
								:key="assignmentId"
								class="d-flex justify-content-between gap-2"
							>
								<span class="text-truncate">
									#{{ index }} · {{ memberLabel(assignmentId) }}
								</span>
								<span class="text-body-secondary text-nowrap">
									{{ positionDelta(assignmentId, index) }}
								</span>
							</li>
						</ul>
						<div v-if="suggestion.conflicts.length > 0" class="small mb-2">
							<div class="fw-semibold">Conflicts</div>
							<ul class="list-unstyled mb-0 text-warning-emphasis">
								<li
									v-for="conflict in suggestion.conflicts"
									:key="conflict.assignmentId"
								>
									#{{ conflict.assignmentId }} — {{ conflict.reason }}
								</li>
							</ul>
						</div>
					</template>
					<div class="d-flex gap-2">
						<button
							v-if="suggestion.improved"
							type="button"
							class="btn btn-sm btn-primary"
							:disabled="busy"
							@click="applySuggestion"
						>
							Apply order
						</button>
						<button
							type="button"
							class="btn btn-sm btn-outline-secondary"
							:disabled="busy"
							@click="dismissSuggestion"
						>
							Dismiss
						</button>
					</div>
				</div>
			</div>
		</div>

		<div v-if="routeError" class="alert alert-danger py-2 small mt-3 mb-0">
			{{ routeError }}
		</div>

		<div v-if="route.state === 'draft'" class="mt-3 pt-3 border-top">
			<button
				v-if="!discardPending"
				type="button"
				class="btn btn-sm btn-outline-danger w-100"
				:disabled="busy"
				@click="discardPending = true"
			>
				<i class="bi bi-trash3 me-1"></i>Discard route
			</button>
			<div v-else>
				<p class="small mb-2">
					Discard route #{{ route.id }}?
					<template v-if="route.members.length > 0">
						Its {{ route.members.length }}
						{{
							route.members.length === 1
								? "assignment returns"
								: "assignments return"
						}}
						to the unassigned pool.
					</template>
					<template v-else>It holds no work.</template>
				</p>
				<div class="d-flex gap-2">
					<button
						type="button"
						class="btn btn-sm btn-danger"
						:disabled="busy"
						@click="discardRoute"
					>
						Discard route
					</button>
					<button
						type="button"
						class="btn btn-sm btn-outline-secondary"
						:disabled="busy"
						@click="discardPending = false"
					>
						Keep route
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { apiClient } from "@/client";
import { formatDate, formatDuration } from "@/format";
import type {
	MapReviewSiteRow,
	OrderSuggestion,
	RoutePlan,
	RoutePlanStatus,
	RoutePlanStop,
	RouteReorderRequest,
	RouteStateUpdate,
	SyncAssignment,
	SyncRoute,
	User,
} from "@/type/api";

import { orderedMembers, unsequencedMembers, type Selection } from "./model";
import {
	describeError,
	kindLabel,
	routeWindowText,
	targetLabel,
	technicianLabel,
} from "./wire";

const props = defineProps<{
	route: SyncRoute;
	users: User[];
	sites: MapReviewSiteRow[];
	assignments: SyncAssignment[];
	/** The Route's drive plan as the page holds it, or null while none is
	 * known — the pane then says so rather than inventing one. */
	plan: RoutePlan | null;
}>();

const emit = defineEmits<{
	notify: [title: string, message: string];
	changed: [];
	select: [selection: Selection];
}>();

const busy = ref(false);
const routeError = ref("");
const addAssignmentId = ref(0);
const dragId = ref<number | null>(null);
const discardPending = ref(false);

const assignmentIndex = computed(
	() => new Map(props.assignments.map((row) => [row.id, row])),
);

const ordered = computed(() => orderedMembers(props.route));
const unsequenced = computed(() => unsequencedMembers(props.route));

const editable = computed(
	() => props.route.state !== "completed" && props.route.state !== "cancelled",
);

// ---------------------------------------------------------------------------
// The drive plan (routing design §10.2). Everything below is a reading of the
// prop: the plan is computed by the server from this Route's members, and this
// card states it. It never writes one, and no action above waits on it.
// ---------------------------------------------------------------------------

/** A plan that states a schedule. The two failure statuses carry no path and no
 * times, so the card prints their reason and nothing that pretends to be a day. */
function usablePlan(plan: RoutePlan): boolean {
	return plan.status === "ok" || plan.status === "partial";
}

/** The plan's stops in sequence order. The server writes them in order; reading
 * them by position means the list still reads top-down if it ever does not. */
const planStops = computed<RoutePlanStop[]>(() => {
	const plan = props.plan;
	if (plan === null || !usablePlan(plan)) return [];
	return [...plan.stops].sort((left, right) => left.position - right.position);
});

/** The one summary line: drive time, service time and the finish, each stated
 * as "not computed" rather than as zero when the plan could not say. A plan the
 * engine never computed has no summary at all — the failure line above it is
 * the whole reading. */
const planSummary = computed<string | null>(() => {
	const plan = props.plan;
	if (plan === null || !usablePlan(plan)) return null;
	const drive =
		plan.travelSeconds === null
			? "drive time not computed"
			: `${formatDuration(plan.travelSeconds)} drive`;
	const service =
		plan.serviceSeconds === null || plan.serviceSeconds === 0
			? "no service time"
			: `${formatDuration(plan.serviceSeconds)} service`;
	const finish =
		plan.finishesAt === null
			? "no finish time"
			: `finish ${formatDate(new Date(plan.finishesAt))}`;
	return `${drive} · ${service} · ${finish}`;
});

const PLAN_STATUS: Record<RoutePlanStatus, { label: string; badge: string }> = {
	ok: { label: "planned", badge: "text-bg-success" },
	partial: { label: "partial", badge: "text-bg-warning" },
	unavailable: { label: "engine down", badge: "text-bg-warning" },
	failed: { label: "failed", badge: "text-bg-danger" },
};

const planStatusLabel = computed(() =>
	props.plan === null ? "" : PLAN_STATUS[props.plan.status].label,
);

const planStatusClass = computed(() =>
	props.plan === null ? "" : PLAN_STATUS[props.plan.status].badge,
);

/** The return leg's time: the finish the plan states less the last departure,
 * which is exactly the leg back to the base (§7.3). Null when there is no base
 * or the last stop never departed. */
const returnSeconds = computed<number | null>(() => {
	const plan = props.plan;
	if (plan === null || plan.base === null || plan.finishesAt === null) {
		return null;
	}
	const stops = planStops.value;
	const departAt = stops[stops.length - 1]?.departAt;
	if (!departAt) return null;
	const finish = new Date(plan.finishesAt).getTime();
	const depart = new Date(departAt).getTime();
	if (Number.isNaN(finish) || Number.isNaN(depart)) return null;
	return Math.max(0, Math.round((finish - depart) / 1000));
});

const returnText = computed<string>(() => {
	const plan = props.plan;
	if (plan === null || !usablePlan(plan)) return "";
	if (plan.base === null) {
		return "No base configured: the day ends at the last stop.";
	}
	const where = plan.base.label ?? "the base";
	const seconds = returnSeconds.value;
	return seconds === null
		? `Return to ${where}.`
		: `Return ${formatDuration(seconds)} to ${where}.`;
});

function etaText(stop: RoutePlanStop): string {
	return stop.arriveAt === null
		? "no ETA"
		: formatDate(new Date(stop.arriveAt));
}

/** What is spent at the stop. Zero is honest rather than guessed: §5.2 resolves
 * the per-kind default and assumes nothing when there is none. */
function serviceText(stop: RoutePlanStop): string {
	return stop.dwellSeconds > 0
		? formatDuration(stop.dwellSeconds)
		: "no time estimate";
}

// The proposal is a read the operator may accept (§8). Its only write is the
// existing reorder door, so the pane never sequences anything by itself.
const suggestion = ref<OrderSuggestion | null>(null);

/** Nothing to propose for a Route whose sequence is already history, and nothing
 * to sequence on a Route holding no work. A cancelled Route is not planned at
 * all (§6.2), so it is never offered the suggestion either. */
const canSuggest = computed(
	() => editable.value && props.route.members.length > 0,
);

/** The positions the Route states now, for the diff: a member missing from it
 * is one the Route has not sequenced yet. */
const currentPositions = computed(() => {
	const positions = new Map<number, number>();
	for (const member of ordered.value) {
		if (member.position !== null) {
			positions.set(member.assignmentId, member.position);
		}
	}
	return positions;
});

function positionDelta(assignmentId: number, position: number): string {
	const before = currentPositions.value.get(assignmentId);
	if (before === undefined) return "was unsequenced";
	if (before === position) return `#${position} (unchanged)`;
	return `was #${before}`;
}

async function suggestOrder() {
	busy.value = true;
	routeError.value = "";
	try {
		suggestion.value = await apiClient.JSONPost<OrderSuggestion>(
			`/api/route/${props.route.id}/order/suggestion`,
			{},
		);
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Order not suggested", routeError.value);
	} finally {
		busy.value = false;
	}
}

// Accepting names the Route's whole membership, which is exactly what the
// reorder door takes — and how a member that was still unsequenced gets placed
// (§8's last paragraph).
async function applySuggestion() {
	const proposal = suggestion.value;
	if (proposal === null) return;
	const applied = await writeOrder(proposal.assignmentIds);
	if (!applied) return;
	suggestion.value = null;
	emit(
		"notify",
		"Order applied",
		`Route #${props.route.id} now runs in the suggested order.`,
	);
}

function dismissSuggestion() {
	suggestion.value = null;
}

// A discard confirmation belongs to the Route it was opened on: selecting
// another Route must not leave the second click armed over a different record,
// and a stale error would read as the new Route's. A proposal belongs to the
// order it was computed from, so it goes with it.
watch(
	() => props.route.id,
	() => {
		discardPending.value = false;
		routeError.value = "";
		suggestion.value = null;
	},
);

/** The technician's draft work no Route holds: the add-member door takes
 * exactly this set, since a Route binds one technician (§8 Rule 2). */
const addable = computed(() =>
	props.assignments.filter(
		(row) =>
			row.routeId === null &&
			row.technicianId === props.route.technicianId &&
			row.state === "draft",
	),
);

function assignmentLabel(assignment: SyncAssignment): string {
	return `#${assignment.id} — ${kindLabel(assignment.kind)} · ${targetLabel(assignment, props.sites)}`;
}

function memberLabel(assignmentId: number): string {
	const row = assignmentIndex.value.get(assignmentId);
	return row
		? `#${assignmentId} — ${kindLabel(row.kind)} · ${targetLabel(row, props.sites)}`
		: `#${assignmentId}`;
}

async function addMember() {
	if (addAssignmentId.value <= 0) return;
	busy.value = true;
	routeError.value = "";
	try {
		// A member joins unsequenced: position null is the normal membership
		// (decision 10), and the Route sequences it on the operator's order.
		await apiClient.JSONPut(`/api/route/${props.route.id}/assignment`, {
			assignment_id: addAssignmentId.value,
			position: null,
		});
		emit(
			"notify",
			"Added to route",
			`Assignment #${addAssignmentId.value} joined route #${props.route.id} unsequenced.`,
		);
		addAssignmentId.value = 0;
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Assignment not added", routeError.value);
	} finally {
		busy.value = false;
	}
}

async function removeMember(assignmentID: number) {
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONDelete(
			`/api/route/${props.route.id}/assignment/${assignmentID}`,
		);
		emit(
			"notify",
			"Removed from route",
			`Assignment #${assignmentID} left route #${props.route.id}.`,
		);
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Assignment not removed", routeError.value);
	} finally {
		busy.value = false;
	}
}

async function writeOrder(assignmentIDs: number[]): Promise<boolean> {
	const body: RouteReorderRequest = { assignment_ids: assignmentIDs };
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPut<SyncRoute>(
			`/api/route/${props.route.id}/order`,
			body,
		);
		emit("changed");
		return true;
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Route order not written", routeError.value);
		return false;
	} finally {
		busy.value = false;
	}
}

// A drop states the route's whole sequence: the ordered members in their new
// order, then whatever is still unsequenced. The door assigns positions
// 0..n-1 to the list it is given, so a drop is also what sequences the tail.
async function dropAt(index: number) {
	const moving = dragId.value;
	dragId.value = null;
	if (moving === null) return;
	const rest = ordered.value
		.map((member) => member.assignmentId)
		.filter((assignmentID) => assignmentID !== moving);
	rest.splice(Math.min(index, rest.length), 0, moving);
	const unsequencedIDs = unsequenced.value
		.map((member) => member.assignmentId)
		.filter((assignmentID) => assignmentID !== moving);
	await writeOrder([...rest, ...unsequencedIDs]);
}

async function dispatchRoute() {
	const body: RouteStateUpdate = { to: "dispatched" };
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPut<SyncRoute>(
			`/api/route/${props.route.id}/state`,
			body,
		);
		emit(
			"notify",
			"Route dispatched",
			`Route #${props.route.id} is on its way.`,
		);
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Route not dispatched", routeError.value);
	} finally {
		busy.value = false;
	}
}

// Discarding is the draft's own action: the server releases the Route's members
// back to the unassigned pool and cancels the Route in one write, so the reload
// behind `changed` shows a cancelled Route holding nothing. It is the one route
// action with no way back, hence the confirm step above.
async function discardRoute() {
	const id = props.route.id;
	const members = props.route.members.length;
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPost<SyncRoute>(`/api/route/${id}/discard`, {});
		discardPending.value = false;
		emit(
			"notify",
			"Route discarded",
			`Route #${id} was discarded${
				members
					? `; ${members} assignment(s) returned to the unassigned pool`
					: ""
			}.`,
		);
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Route not discarded", routeError.value);
	} finally {
		busy.value = false;
	}
}
</script>
